import { useEffect, useId, useRef, useState } from "react";
import { PhoneInput } from "react-international-phone";
import axiosInstance from "../../services/authWrapper";
import constants from "../../Utils/constants";
import useTranslator from "../../hooks/useTranslator";
import { getCountryLabelKey } from "../../pages/onboarding/authenticateYourId/documentSelection";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import { isValidEmail, toast, validateDniNie } from "../../helper/common";
import {
  Button,
  Checkbox,
  ChoiceGroup,
  Icon,
  Select,
  TextField,
} from "../design";
import "./ClientIdentificationForm.css";
const personNamePattern = /^[A-Za-zÀ-ÿ\s'-]+$/;
const personNameFields = new Set([
  "first_name",
  "last_name",
  "beneficial_owner_name",
  "administrator_name",
  "owner_pep_person",
  "signatory_name",
  "representative_name",
]);
const identityNumberFields = new Set([
  "document_number",
  "company_document_number",
  "beneficial_owner_nif",
  "administrator_nif",
  "signatory_document_number",
  "representative_nif",
]);
const postalCodeFields = new Set(["postal_code", "company_postal_code"]);

const preventInvalidNameInput = (event) => {
  if (event.data && !personNamePattern.test(event.data)) event.preventDefault();
};

const restrictKeyInput = (event, pattern) => {
  if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1)
    return;
  if (!pattern.test(event.key)) event.preventDefault();
};

const fundOptions = [
  ["MY_ACTIVITY", "viviaFundActivity"],
  ["FINANCING", "viviaFundFinancing"],
  ["SAVINGS", "viviaFundSavings"],
  ["INHERITANCE", "viviaFundInheritance"],
  ["SALE_OF_ASSETS", "viviaFundSaleOfAssets"],
  ["PRIZES_OR_LOTTERY", "viviaFundPrizesOrLotteries"],
  ["OTHER", "viviaFundOther"],
];
const employmentOptions = [
  [constants.employed, "employed"],
  [constants.self_employed, "selfEmployed"],
  [constants.retired, "retired"],
  ["other", "viviaOtherEmployment"],
];
const incomeOptions = [
  ["LT_15K", "viviaIncomeUnder15000"],
  ["15K_30K", "viviaIncome15000To30000"],
  ["30K_50K", "viviaIncome30000To50000"],
  ["GT_50K", "viviaIncomeOver50000"],
];
const toClientIdentificationEmploymentStatus = (value) => {
  if (!value) return "";
  if (
    [constants.employed, constants.self_employed, constants.retired].includes(
      value
    )
  )
    return value;
  return "other";
};
const toClientIdentificationIncomeBracket = (value) => {
  const numericValue = Number(value);
  if (value !== "" && value != null && Number.isFinite(numericValue)) {
    if (numericValue < 15000) return "LT_15K";
    if (numericValue < 30000) return "15K_30K";
    if (numericValue <= 50000) return "30K_50K";
    return "GT_50K";
  }
  const brackets = {
    under15000: "LT_15K",
    "15000To30000": "15K_30K",
    "30000To50000": "30K_50K",
    "50000To75000": "GT_50K",
    over75000: "GT_50K",
  };
  return brackets[value] || value || "";
};
const toClientIdentificationLegalForm = (value) => {
  const legalForms = {
    sa: "SA",
    public_limited_company: "SA",
    sl: "SL",
    limited_liability_company: "SL",
  };
  return (
    legalForms[String(value || "").toLowerCase()] || (value ? "OTHER" : "")
  );
};
const toDateInputValue = (value) => (value ? String(value).split("T")[0] : "");
const hasPrefillValue = (value) =>
  value !== null && value !== undefined && value !== "";
const storedValue = (group, key, fallback = "") => {
  const field = group?.[key];
  const value =
    field &&
    typeof field === "object" &&
    !Array.isArray(field) &&
    Object.prototype.hasOwnProperty.call(field, "value")
      ? field.value
      : field;
  return hasPrefillValue(value) ? value : fallback;
};
const storedSource = (group, key) => group?.[key]?.source || "";
const normalizeDocumentTypeForForm = (value) =>
  ["DNI", "NIE", "PASSPORT"].includes(String(value || "").toUpperCase())
    ? String(value).toUpperCase()
    : "";
const comparableValue = (value) =>
  Array.isArray(value) ? [...value].sort() : (value ?? null);
const valuesMatch = (left, right) =>
  JSON.stringify(comparableValue(left)) ===
  JSON.stringify(comparableValue(right));

const countryDisplayValue = (value, _t) => {
  if (!value) return "";
  const originalValue = String(value).trim();
  const countryCode = originalValue.toUpperCase();
  const labelKey = getCountryLabelKey(countryCode);
  return labelKey === countryCode ? originalValue : _t(labelKey);
};

const sourceFieldPaths = {
  first_name: ["personal_information", "first_name"],
  last_name: ["personal_information", "last_name"],
  document_type: ["personal_information", "document_type"],
  document_number: ["personal_information", "document_number"],
  country_of_birth: ["personal_information", "country_of_birth"],
  country_of_residence: ["personal_information", "country_of_residence"],
  address: ["personal_information", "address"],
  postal_code: ["personal_information", "postal_code"],
  town: ["personal_information", "town"],
  province: ["personal_information", "province"],
  phone: ["personal_information", "phone"],
  email: ["personal_information", "email"],
  operation_capacity: ["personal_information", "operation_capacity"],
  representative_name: ["personal_information", "representative_name"],
  representative_nif: ["personal_information", "representative_nif"],
  is_pep: ["public_positions", "is_pep"],
  is_pep_relative_or_associate: [
    "public_positions",
    "is_pep_relative_or_associate",
  ],
  pep_relationship: ["public_positions", "pep_relationship"],
  pep_position: ["public_positions", "pep_position"],
  pep_country: ["public_positions", "pep_country"],
  pep_relative_position: ["public_positions", "pep_relative_position"],
  pep_relative_country: ["public_positions", "pep_relative_country"],
  employment_status: ["professional_activity", "employment_status"],
  annual_income: ["professional_activity", "annual_income"],
  employer_name: ["professional_activity", "employer_name"],
  job_position: ["professional_activity", "job_position"],
  professional_activity: ["professional_activity", "activity"],
  business_name: ["professional_activity", "business_name"],
  business_activity: ["professional_activity", "business_activity"],
  ownership_percent: ["professional_activity", "ownership_percent"],
  business_start_year: ["professional_activity", "business_start_year"],
  annual_turnover: ["professional_activity", "annual_turnover"],
  previous_activity: ["professional_activity", "previous_activity"],
  employment_status_other: ["professional_activity", "employment_status_other"],
  company_name: ["company_information", "company_name"],
  legal_form: ["company_information", "legal_form"],
  legal_form_other: ["company_information", "legal_form_other"],
  incorporation_date: ["company_information", "incorporation_date"],
  company_document_type: ["company_information", "document_type"],
  company_document_number: ["company_information", "document_number"],
  registered_address: ["company_information", "registered_address"],
  company_postal_code: ["company_information", "postal_code"],
  company_town: ["company_information", "town"],
  company_province: ["company_information", "province"],
  company_country: ["company_information", "country"],
  has_beneficial_owner: ["beneficial_owners", "has_beneficial_owner"],
  beneficial_owner_is_pep: ["beneficial_owners", "beneficial_owner_is_pep"],
  beneficial_owner_is_pep_relative_or_associate: [
    "beneficial_owners",
    "beneficial_owner_is_pep_relative_or_associate",
  ],
  beneficial_owner_name: ["beneficial_owners", "beneficial_owners"],
  beneficial_owner_nif: ["beneficial_owners", "beneficial_owners"],
  administration_type: ["beneficial_owners", "administration_type"],
  administrator_name: ["beneficial_owners", "administrators"],
  administrator_nif: ["beneficial_owners", "administrators"],
  owner_pep_relationship: ["beneficial_owners", "pep_relationship"],
  owner_pep_person: ["beneficial_owners", "pep_person"],
  owner_pep_position: ["beneficial_owners", "pep_position"],
  owner_pep_country: ["beneficial_owners", "pep_country"],
  owner_pep_relative_position: ["beneficial_owners", "pep_relative_position"],
  owner_pep_relative_country: ["beneficial_owners", "pep_relative_country"],
  signatory_name: ["signatory", "full_name"],
  signatory_document_number: ["signatory", "document_number"],
  signing_capacity: ["signatory", "signing_capacity"],
  company_position: ["signatory", "company_position"],
  signatory_phone: ["signatory", "phone"],
  signatory_email: ["signatory", "email"],
  activity_description: ["company_activity", "description"],
  is_listed_company: ["company_activity", "is_listed_company"],
  belongs_to_company_group: ["company_activity", "belongs_to_company_group"],
  group_name: ["company_activity", "group_name"],
  sources: ["source_of_funds", "sources"],
  other_source: ["source_of_funds", "other"],
  funds_country: ["source_of_funds", "country"],
  funds_country_other: ["source_of_funds", "country_other"],
  payment_method: ["source_of_funds", "payment_method"],
  banking_entity: ["source_of_funds", "banking_entity"],
};

const getFieldSources = (data = {}) =>
  Object.entries(sourceFieldPaths).reduce((result, [field, [group, key]]) => {
    result[field] = storedSource(data[group], key);
    return result;
  }, {});
const buildProfilePrefill = (user = {}) => {
  // KYC record fields (see helper/common.js "KYC data").
  const identity = user?.onfido_user_kyc?.kyc_data || {};
  const identityDocumentType = String(
    identity?.document_type || ""
  ).toLowerCase();
  const isIdentityDniNie =
    identityDocumentType === "national_identity_card" ||
    (identityDocumentType === "residence_permit" &&
      identity?.issuing_country === "ESP");
  const identityDocumentNumber = isIdentityDniNie
    ? identity?.personal_number
    : identity?.document_number;
  const forensicAnalysis = user?.tenant_extra_field_id?.forensic_analysis || {};
  const employmentHistory =
    forensicAnalysis?.["Employment History"]?.dataInJSON?.employmentHistory;
  const currentEmployment = Array.isArray(employmentHistory)
    ? employmentHistory.find(
        (employment) => employment?.isCurrentEmployer || !employment?.jobEndOn
      ) || employmentHistory[0]
    : null;
  const bankProvider =
    user?.bank_details?.userDataByProvider?.[0]?.providerName || "";
  const address = user?.address || user?.address1 || "";
  const town = user?.town || user?.city || user?.municipality || "";
  const province = user?.province || user?.region || user?.state || "";
  const residenceCountry =
    user?.country_of_residence ||
    user?.residence_country ||
    user?.country ||
    "";

  return {
    first_name: identity?.first_name || user?.fname || "",
    last_name: identity?.last_name || user?.lname || "",
    document_type: identityDocumentNumber
      ? isIdentityDniNie
        ? /^[XYZ]/i.test(identityDocumentNumber)
          ? "NIE"
          : "DNI"
        : "PASSPORT"
      : user?.dni_nie
        ? /^[XYZ]/i.test(user.dni_nie)
          ? "NIE"
          : "DNI"
        : user?.passport_id
          ? "PASSPORT"
          : "",
    document_type_other: "",
    document_number:
      identityDocumentNumber || user?.dni_nie || user?.passport_id || "",
    country_of_birth: identity?.nationality || user?.nationality || "",
    country_of_residence: residenceCountry,
    address,
    postal_code: user?.post_code || "",
    town,
    province,
    phone: user?.phone_number || "",
    email: user?.email || "",
    operation_capacity: "DIRECT",
    representative_name: "",
    representative_nif: "",
    is_pep: null,
    is_pep_relative_or_associate: null,
    pep_relationship: "",
    pep_position: "",
    pep_country: "",
    pep_relative_position: "",
    pep_relative_country: "",
    employment_status: toClientIdentificationEmploymentStatus(
      user?.employment_status
    ),
    annual_income: toClientIdentificationIncomeBracket(
      user?.current_annual_income
    ),
    employer_name:
      currentEmployment?.employerName ||
      user?.employer_name ||
      user?.employer ||
      "",
    job_position:
      currentEmployment?.jobPosition ||
      currentEmployment?.jobTitle ||
      user?.job_position ||
      user?.job_title ||
      "",
    professional_activity: user?.employment_category || "",
    business_name: user?.business_name || "",
    business_activity: user?.business_activity || "",
    ownership_percent: user?.ownership_percent ?? "",
    business_start_year: user?.business_start_year || "",
    annual_turnover: user?.annual_turnover ?? "",
    previous_activity: user?.previous_activity || "",
    employment_status_other: "",
    company_name: user?.company_legal_name || "",
    legal_form: toClientIdentificationLegalForm(user?.company_type),
    legal_form_other: ["SA", "SL"].includes(
      toClientIdentificationLegalForm(user?.company_type)
    )
      ? ""
      : user?.company_type || "",
    incorporation_date: toDateInputValue(user?.company_incorporation_date),
    company_document_type: user?.company_cif ? "CIF_NIF" : "",
    company_document_number: user?.company_cif || "",
    registered_address: user?.company_registered_address || address,
    company_postal_code: user?.company_postal_code || user?.post_code || "",
    company_town: user?.company_town || town,
    company_province: user?.company_province || province,
    company_country: user?.company_country || residenceCountry,
    has_beneficial_owner: null,
    beneficial_owner_is_pep: null,
    beneficial_owner_is_pep_relative_or_associate: null,
    beneficial_owner_name: "",
    beneficial_owner_nif: "",
    administration_type: "",
    administrator_name: "",
    administrator_nif: "",
    owner_pep_relationship: "",
    owner_pep_person: "",
    owner_pep_position: "",
    owner_pep_country: "",
    owner_pep_relative_position: "",
    owner_pep_relative_country: "",
    signatory_name:
      `${identity?.first_name || user?.fname || ""} ${identity?.last_name || user?.lname || ""}`.trim(),
    signatory_document_number:
      identityDocumentNumber || user?.dni_nie || user?.passport_id || "",
    signing_capacity: "",
    company_position: user?.company_representative_position || "",
    signatory_phone: user?.phone_number || "",
    signatory_email: user?.email || "",
    activity_description: user?.company_cnae || "",
    is_listed_company: null,
    belongs_to_company_group: null,
    group_name: "",
    funds_country: "",
    funds_country_other: "",
    payment_method: bankProvider ? "BANK_TRANSFER" : "",
    banking_entity: bankProvider,
    other_source: "",
  };
};
const mergeStoredIdentificationData = (profile, saved) => {
  if (!saved) return profile;
  const personal = saved.personal_information;
  const publicPositions = saved.public_positions;
  const professional = saved.professional_activity;
  const company = saved.company_information;
  const owners = saved.beneficial_owners;
  const signatory = saved.signatory;
  const activity = saved.company_activity;
  const funds = saved.source_of_funds;
  const beneficialOwner = storedValue(owners, "beneficial_owners", [])[0] || {};
  const administrator = storedValue(owners, "administrators", [])[0] || {};

  return {
    ...profile,
    first_name: storedValue(personal, "first_name", profile.first_name),
    last_name: storedValue(personal, "last_name", profile.last_name),
    document_type: normalizeDocumentTypeForForm(
      storedValue(personal, "document_type", profile.document_type)
    ),
    document_type_other: storedValue(personal, "document_type_other"),
    document_number: storedValue(
      personal,
      "document_number",
      profile.document_number
    ),
    country_of_birth: storedValue(
      personal,
      "country_of_birth",
      profile.country_of_birth
    ),
    country_of_residence: storedValue(
      personal,
      "country_of_residence",
      profile.country_of_residence
    ),
    address: storedValue(personal, "address", profile.address),
    postal_code: storedValue(personal, "postal_code", profile.postal_code),
    town: storedValue(personal, "town", profile.town),
    province: storedValue(personal, "province", profile.province),
    phone: storedValue(personal, "phone", profile.phone),
    email: storedValue(personal, "email", profile.email),
    operation_capacity: storedValue(
      personal,
      "operation_capacity",
      profile.operation_capacity
    ),
    representative_name: storedValue(personal, "representative_name"),
    representative_nif: storedValue(personal, "representative_nif"),
    is_pep: storedValue(publicPositions, "is_pep", profile.is_pep),
    is_pep_relative_or_associate: storedValue(
      publicPositions,
      "is_pep_relative_or_associate",
      profile.is_pep_relative_or_associate
    ),
    pep_relationship: storedValue(publicPositions, "pep_relationship"),
    pep_position: storedValue(publicPositions, "pep_position"),
    pep_country: storedValue(publicPositions, "pep_country"),
    pep_relative_position: storedValue(
      publicPositions,
      "pep_relative_position"
    ),
    pep_relative_country: storedValue(publicPositions, "pep_relative_country"),
    employment_status: storedValue(
      professional,
      "employment_status",
      profile.employment_status
    ),
    annual_income: toClientIdentificationIncomeBracket(
      storedValue(professional, "annual_income", profile.annual_income)
    ),
    employer_name: storedValue(
      professional,
      "employer_name",
      profile.employer_name
    ),
    job_position: storedValue(
      professional,
      "job_position",
      profile.job_position
    ),
    professional_activity: storedValue(
      professional,
      "activity",
      storedValue(
        professional,
        "employer_activity",
        profile.professional_activity
      )
    ),
    business_name: storedValue(professional, "business_name"),
    business_activity: storedValue(professional, "business_activity"),
    ownership_percent: storedValue(professional, "ownership_percent"),
    business_start_year: storedValue(professional, "business_start_year"),
    annual_turnover: storedValue(professional, "annual_turnover"),
    previous_activity: storedValue(professional, "previous_activity"),
    employment_status_other: storedValue(
      professional,
      "employment_status_other"
    ),
    company_name: storedValue(company, "company_name", profile.company_name),
    legal_form: storedValue(company, "legal_form", profile.legal_form),
    legal_form_other: storedValue(company, "legal_form_other"),
    incorporation_date: toDateInputValue(
      storedValue(company, "incorporation_date", profile.incorporation_date)
    ),
    company_document_type: storedValue(
      company,
      "document_type",
      profile.company_document_type
    ),
    company_document_number: storedValue(
      company,
      "document_number",
      profile.company_document_number
    ),
    registered_address: storedValue(
      company,
      "registered_address",
      profile.registered_address
    ),
    company_postal_code: storedValue(
      company,
      "postal_code",
      profile.company_postal_code
    ),
    company_town: storedValue(
      company,
      "town",
      storedValue(company, "city", profile.company_town)
    ),
    company_province: storedValue(
      company,
      "province",
      storedValue(company, "region", profile.company_province)
    ),
    company_country: storedValue(company, "country", profile.company_country),
    has_beneficial_owner: storedValue(
      owners,
      "has_beneficial_owner",
      profile.has_beneficial_owner
    ),
    beneficial_owner_is_pep: storedValue(
      owners,
      "beneficial_owner_is_pep",
      profile.beneficial_owner_is_pep
    ),
    beneficial_owner_is_pep_relative_or_associate: storedValue(
      owners,
      "beneficial_owner_is_pep_relative_or_associate",
      profile.beneficial_owner_is_pep_relative_or_associate
    ),
    beneficial_owner_name: storedValue(beneficialOwner, "full_name"),
    beneficial_owner_nif: storedValue(beneficialOwner, "nif"),
    administration_type: storedValue(owners, "administration_type"),
    administrator_name: storedValue(administrator, "full_name"),
    administrator_nif: storedValue(administrator, "nif"),
    owner_pep_relationship: storedValue(owners, "pep_relationship"),
    owner_pep_person: storedValue(owners, "pep_person"),
    owner_pep_position: storedValue(owners, "pep_position"),
    owner_pep_country: storedValue(owners, "pep_country"),
    owner_pep_relative_position: storedValue(owners, "pep_relative_position"),
    owner_pep_relative_country: storedValue(owners, "pep_relative_country"),
    signatory_name: storedValue(signatory, "full_name", profile.signatory_name),
    signatory_document_number: storedValue(
      signatory,
      "document_number",
      profile.signatory_document_number
    ),
    signing_capacity: storedValue(signatory, "signing_capacity"),
    company_position: storedValue(
      signatory,
      "company_position",
      profile.company_position
    ),
    signatory_phone: storedValue(signatory, "phone", profile.signatory_phone),
    signatory_email: storedValue(signatory, "email", profile.signatory_email),
    activity_description: storedValue(
      activity,
      "description",
      profile.activity_description
    ),
    is_listed_company: storedValue(
      activity,
      "is_listed_company",
      profile.is_listed_company
    ),
    belongs_to_company_group: storedValue(
      activity,
      "belongs_to_company_group",
      profile.belongs_to_company_group
    ),
    group_name: storedValue(activity, "group_name"),
    funds_country: storedValue(funds, "country", profile.funds_country),
    funds_country_other: storedValue(funds, "country_other"),
    payment_method: storedValue(
      funds,
      "payment_method",
      profile.payment_method
    ),
    banking_entity: storedValue(
      funds,
      "banking_entity",
      profile.banking_entity
    ),
    other_source: storedValue(funds, "other"),
  };
};
const individualRequired = [
  "first_name",
  "last_name",
  "document_type",
  "document_number",
  "country_of_birth",
  "country_of_residence",
  "address",
  "postal_code",
  "town",
  "province",
  "phone",
  "email",
  "operation_capacity",
  "is_pep",
  "is_pep_relative_or_associate",
  "employment_status",
  "funds_country",
  "payment_method",
];
const companyRequired = [
  "company_name",
  "legal_form",
  "incorporation_date",
  "company_document_type",
  "company_document_number",
  "registered_address",
  "company_postal_code",
  "company_town",
  "company_province",
  "company_country",
  "has_beneficial_owner",
  "beneficial_owner_is_pep",
  "beneficial_owner_is_pep_relative_or_associate",
  "signatory_name",
  "signatory_document_number",
  "signing_capacity",
  "company_position",
  "signatory_phone",
  "signatory_email",
  "activity_description",
  "is_listed_company",
  "belongs_to_company_group",
  "funds_country",
  "payment_method",
];
const sourcePresentation = {
  [constants.CLIENT_IDENTIFICATION_SOURCES.ID]: [
    "/images/id-document.svg",
    "viviaFromYourId",
  ],
  [constants.CLIENT_IDENTIFICATION_SOURCES.RECORDS]: [
    "/images/user-icon.svg",
    "viviaFromYourRecords",
  ],
  [constants.CLIENT_IDENTIFICATION_SOURCES.PAYSLIP]: [
    "/images/document.svg",
    "viviaFromYourPayslip",
  ],
  [constants.CLIENT_IDENTIFICATION_SOURCES.BANK]: [
    "/images/bank-icon.svg",
    "viviaFromYourBank",
  ],
  [constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER]: [
    "/images/user-icon.svg",
    "viviaEditedByYou",
  ],
};

const SourceNote = ({ source, _t }) => {
  const presentation = sourcePresentation[source];
  if (!presentation) return null;
  return (
    <small className="cif-source">
      <img src={presentation[0]} alt="" aria-hidden="true" />
      {source !== constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER && (
        <>
          <span>{_t("viviaPrefilled")}</span>
          <span aria-hidden="true">·</span>
        </>
      )}
      <span>{_t(presentation[1])}</span>
    </small>
  );
};

const Field = ({ label, error, wide, sourceNote, children }) => (
  <label className={`cif-field${wide ? " cif-field-wide" : ""}`}>
    <span className="cif-label">
      {label} <b>*</b>
    </span>
    {children}
    {sourceNote}
    {error && <small className="cif-error">{error}</small>}
  </label>
);
const Choice = ({ label, name, value, onChange, yes, no, sourceNote }) => (
  <div className="cif-source-control">
    <ChoiceGroup
      name={name}
      legend={
        <>
          {label} <b>*</b>
        </>
      }
      value={value === true ? "true" : value === false ? "false" : ""}
      onChange={(e) => onChange(e.target.value === "true")}
      options={[
        { value: "true", label: yes },
        { value: "false", label: no },
      ]}
    />
    {sourceNote}
  </div>
);
const Segment = ({ label, value, onChange, options, sourceNote }) => {
  const name = useId();
  return (
    <div className="cif-source-control">
      <ChoiceGroup
        name={name}
        legend={
          <>
            {label} <b>*</b>
          </>
        }
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        options={options.map(([key, text]) => ({ value: key, label: text }))}
      />
      {sourceNote}
    </div>
  );
};
const Section = ({
  number,
  total,
  title,
  description,
  pending = 0,
  sectionLabel,
  ofLabel,
  pendingLabel,
  children,
}) => (
  <section
    className="cif-section"
    data-pending={pending > 0 ? "true" : "false"}
  >
    <span className="cif-section-count">
      {sectionLabel} {number} {ofLabel} {total}
    </span>
    <div className="cif-section-body">
      <div className="cif-section-heading">
        <h2>{title}</h2>
        {pending > 0 && (
          <span>
            {pending} {pendingLabel}
          </span>
        )}
      </div>
      {description && <p className="cif-description">{description}</p>}
      {children}
    </div>
  </section>
);
const FormSection = (props) => {
  const { _t } = useTranslator();
  return (
    <Section
      {...props}
      sectionLabel={_t("viviaSection")}
      ofLabel={_t("viviaOf")}
      pendingLabel={_t("viviaPending")}
    />
  );
};
export default function ClientIdentificationForm({
  user,
  workspace,
  applicationId = null,
  guarantorForTenantId = null,
  landlordEmail = null,
  onClose = null,
  onSubmitted,
}) {
  const { _t } = useTranslator();
  const isCompany = user?.employment_status === constants.company;
  const total = isCompany ? 6 : 5;
  const [submitting, setSubmitting] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(true);
  const [signed, setSigned] = useState(false);
  const [sources, setSources] = useState([]);
  const [fieldSources, setFieldSources] = useState({});
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(() => buildProfilePrefill(user));
  const originalPrefill = useRef({});

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const profile = buildProfilePrefill(user);
    profile.country_of_birth = countryDisplayValue(
      profile.country_of_birth,
      _t
    );
    profile.country_of_residence = countryDisplayValue(
      profile.country_of_residence,
      _t
    );
    profile.company_country = countryDisplayValue(profile.company_country, _t);

    setForm(profile);
    setSources([]);
    setSigned(false);
    setFieldSources({});
    setLoadingPrefill(true);

    axiosInstance
      .get(
        `${process.env.REACT_APP_SERVER_URL}/tenants/client-identification-data`,
        {
          params: {
            application_id: applicationId,
            landlord_email: landlordEmail,
          },
        }
      )
      .then((response) => {
        if (!active) return;
        const prefill =
          response?.data?.success_res?.client_identification_data || {};
        const nextForm = mergeStoredIdentificationData(profile, prefill);
        nextForm.country_of_birth = countryDisplayValue(
          nextForm.country_of_birth,
          _t
        );
        nextForm.country_of_residence = countryDisplayValue(
          nextForm.country_of_residence,
          _t
        );
        nextForm.company_country = countryDisplayValue(
          nextForm.company_country,
          _t
        );
        const nextFieldSources = getFieldSources(prefill);
        const savedSources = storedValue(
          prefill.source_of_funds,
          "sources",
          []
        );
        const nextSources = Array.isArray(savedSources) ? savedSources : [];

        setForm(nextForm);
        setFieldSources(nextFieldSources);
        setSources(nextSources);
        setSigned(storedValue(prefill.signature, "accepted", false) === true);
        originalPrefill.current = Object.keys(sourceFieldPaths).reduce(
          (result, key) => {
            result[key] = {
              value: key === "sources" ? nextSources : nextForm[key],
              source: nextFieldSources[key] || "",
            };
            return result;
          },
          {}
        );
      })
      .catch(() => {
        originalPrefill.current = Object.keys(sourceFieldPaths).reduce(
          (result, key) => {
            result[key] = {
              value: key === "sources" ? [] : profile[key],
              source: "",
            };
            return result;
          },
          {}
        );
      })
      .finally(() => {
        if (active) setLoadingPrefill(false);
      });

    return () => {
      active = false;
    };
  }, [user]);
  const isMissing = (value) =>
    value == null || (typeof value === "string" && value.trim() === "");
  const countMissing = (keys) =>
    keys.filter((key) => isMissing(form[key])).length;
  const employmentStatus = String(form.employment_status || "").toLowerCase();
  const employmentRequired = employmentStatus.includes("self")
    ? [
        "business_name",
        "business_activity",
        "ownership_percent",
        "business_start_year",
        "annual_turnover",
      ]
    : employmentStatus === String(constants.retired).toLowerCase()
      ? ["previous_activity"]
      : employmentStatus === "other"
        ? ["employment_status_other"]
        : [
            "annual_income",
            "employer_name",
            "professional_activity",
            "job_position",
          ];
  const individualConditionalRequired = [
    ...(form.operation_capacity === "REPRESENTATIVE"
      ? ["representative_name", "representative_nif"]
      : []),
    ...(form.is_pep ? ["pep_position", "pep_country"] : []),
    ...(form.is_pep_relative_or_associate
      ? ["pep_relationship", "pep_relative_position", "pep_relative_country"]
      : []),
    ...employmentRequired,
  ];
  const companyConditionalRequired = [
    ...(form.legal_form === "OTHER" ? ["legal_form_other"] : []),
    ...(form.has_beneficial_owner
      ? ["beneficial_owner_name", "beneficial_owner_nif"]
      : form.has_beneficial_owner === false
        ? ["administration_type", "administrator_name", "administrator_nif"]
        : []),
    ...(form.beneficial_owner_is_pep
      ? ["owner_pep_person", "owner_pep_position", "owner_pep_country"]
      : []),
    ...(form.beneficial_owner_is_pep_relative_or_associate
      ? [
          "owner_pep_relationship",
          "owner_pep_relative_position",
          "owner_pep_relative_country",
        ]
      : []),
    ...(form.belongs_to_company_group ? ["group_name"] : []),
  ];
  const commonConditionalRequired = [
    ...(sources.includes("OTHER") ? ["other_source"] : []),
    ...(form.funds_country === "OTHER" ? ["funds_country_other"] : []),
    ...(form.payment_method === "BANK_TRANSFER" ? ["banking_entity"] : []),
  ];
  const required = [
    ...(isCompany ? companyRequired : individualRequired),
    ...(isCompany ? companyConditionalRequired : individualConditionalRequired),
    ...commonConditionalRequired,
  ];
  const fundsPending =
    countMissing([
      "funds_country",
      "payment_method",
      ...commonConditionalRequired,
    ]) + (sources.length ? 0 : 1);
  const missing =
    countMissing(required) + (sources.length ? 0 : 1) + (signed ? 0 : 1);
  const individualSectionPending = {
    personal: countMissing([
      "first_name",
      "last_name",
      "document_type",
      "document_number",
      "country_of_birth",
      "country_of_residence",
      "address",
      "postal_code",
      "town",
      "province",
      "phone",
      "email",
      "operation_capacity",
      ...individualConditionalRequired.filter((key) =>
        [
          "document_type_other",
          "representative_name",
          "representative_nif",
        ].includes(key)
      ),
    ]),
    publicPositions: countMissing([
      "is_pep",
      "is_pep_relative_or_associate",
      ...individualConditionalRequired.filter((key) => key.startsWith("pep_")),
    ]),
    professional: countMissing(["employment_status", ...employmentRequired]),
  };
  const companySectionPending = {
    information: countMissing([
      "company_name",
      "legal_form",
      "incorporation_date",
      "company_document_type",
      "company_document_number",
      "registered_address",
      "company_postal_code",
      "company_town",
      "company_province",
      "company_country",
      ...(form.legal_form === "OTHER" ? ["legal_form_other"] : []),
    ]),
    owners: countMissing([
      "has_beneficial_owner",
      "beneficial_owner_is_pep",
      "beneficial_owner_is_pep_relative_or_associate",
      ...companyConditionalRequired.filter(
        (key) =>
          key.startsWith("beneficial_") ||
          key.startsWith("administrator") ||
          key.startsWith("administration") ||
          key.startsWith("owner_pep")
      ),
    ]),
    signatory: countMissing([
      "signatory_name",
      "signatory_document_number",
      "signing_capacity",
      "company_position",
      "signatory_phone",
      "signatory_email",
    ]),
    activity: countMissing([
      "activity_description",
      "is_listed_company",
      "belongs_to_company_group",
      ...(form.belongs_to_company_group ? ["group_name"] : []),
    ]),
  };
  const update = (key, value) => {
    setForm((old) => ({
      ...old,
      [key]: value,
    }));
    setErrors((old) => ({
      ...old,
      [key]: undefined,
    }));
    const original = originalPrefill.current[key];
    setFieldSources((old) => ({
      ...old,
      [key]: valuesMatch(value, original?.value)
        ? original?.source || ""
        : constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER,
    }));
  };
  const toggleSource = (value) => {
    setSources((old) => {
      const next = old.includes(value)
        ? old.filter((item) => item !== value)
        : [...old, value];
      const original = originalPrefill.current.sources;
      setFieldSources((current) => ({
        ...current,
        sources: valuesMatch(next, original?.value)
          ? original?.source || ""
          : constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER,
      }));
      return next;
    });
    setErrors((old) => ({
      ...old,
      sources: undefined,
    }));
  };
  const input = (key, type = "text", extra = {}) => {
    const restrictions = personNameFields.has(key)
      ? { onBeforeInput: preventInvalidNameInput }
      : identityNumberFields.has(key)
        ? {
            maxLength: 9,
            onKeyDown: (event) => restrictKeyInput(event, /^[A-Za-z0-9]$/),
          }
        : postalCodeFields.has(key)
          ? {
              inputMode: "numeric",
              maxLength: 6,
              onKeyDown: (event) => restrictKeyInput(event, /^[0-9]$/),
            }
          : {};

    return (
      <TextField
        id={key}
        type={type}
        value={form[key]}
        onChange={(event) => update(key, event.target.value)}
        error={!!errors[key]}
        aria-invalid={!!errors[key]}
        {...restrictions}
        {...extra}
      />
    );
  };
  const select = (key, options, allowCurrentOption = true) => (
    <Select
      id={key}
      value={form[key]}
      onChange={(event) => update(key, event.target.value)}
      error={!!errors[key]}
      aria-invalid={!!errors[key]}
      options={[
        { value: "", label: _t("pleaseSelect") },
        ...(allowCurrentOption &&
        !options.some(([value]) => value === form[key]) &&
        form[key]
          ? [{ value: form[key], label: form[key] }]
          : []),
        ...options.map(([value, label]) => ({ value, label })),
      ]}
    />
  );
  const phoneInput = (key) => (
    <PhoneInput
      className={`phone_input_info cif-phone-input${
        errors[key] ? " cif-phone-error" : ""
      }`}
      international
      defaultCountry="es"
      value={form[key] || ""}
      onChange={(value) => update(key, value)}
    />
  );
  const yes = _t("yes");
  const no = _t("no");
  const sourceNote = (key) =>
    originalPrefill.current[key]?.source ? (
      <SourceNote source={fieldSources[key]} _t={_t} />
    ) : null;
  const field = (key, label, child, options = {}) => (
    <Field
      label={label}
      error={errors[key]}
      sourceNote={sourceNote(key)}
      {...options}
    >
      {child}
    </Field>
  );
  const validate = () => {
    const next = {};
    required.forEach((key) => {
      if (form[key] === "" || form[key] == null)
        next[key] = _t("requiredField");
    });
    const identity = (key, checkDni) => {
      const value = form[key]?.trim();
      if (!value) return;
      if (!/^[a-zA-Z0-9]+$/.test(value))
        next[key] = _t("onlyLettersAndNumbersAreRequired");
      else if (value.length > 9)
        next[key] = _t("documentNumberCannotExceedNineCharacters");
      else if (checkDni) {
        const kind = /^[XYZ]/i.test(value) ? "nie" : "dni";
        if (!validateDniNie(value, kind))
          next[key] = _t(kind === "nie" ? "invalidnie" : "invaliddni");
      }
    };
    if (isCompany) {
      identity("signatory_document_number", true);
      if (form.has_beneficial_owner) identity("beneficial_owner_nif", true);
      if (form.has_beneficial_owner === false)
        identity("administrator_nif", true);
      if (
        form.company_document_number &&
        !/^[A-Za-z][0-9]{7}[0-9A-Za-z]$/i.test(form.company_document_number)
      )
        next.company_document_number = _t("enterAValidCif");
    } else {
      if (form.first_name && !personNamePattern.test(form.first_name.trim()))
        next.first_name = _t("firstNameInvalid");
      if (form.last_name && !personNamePattern.test(form.last_name.trim()))
        next.last_name = _t("lnameNameInvalid");
      identity("document_number", ["DNI", "NIE"].includes(form.document_type));
      if (form.operation_capacity === "REPRESENTATIVE")
        identity("representative_nif", true);
    }
    const email = isCompany ? "signatory_email" : "email";
    const phone = isCompany ? "signatory_phone" : "phone";
    const postal = isCompany ? "company_postal_code" : "postal_code";
    if (form[email] && !isValidEmail(form[email]))
      next[email] = _t("invalidEmailFormat");
    const phoneDigits = String(form[phone] || "").replace(/\D/g, "");
    if (form[phone] && phoneDigits.length < 6)
      next[phone] = _t("phoneNumberMustBeAtLeastSixDigits");
    if (form[postal] && form[postal].length > 6)
      next[postal] = _t("postCodeCannotExpeedSixCharacters");
    if (
      form.ownership_percent !== "" &&
      (Number(form.ownership_percent) < 0 ||
        Number(form.ownership_percent) > 100)
    )
      next.ownership_percent = _t("viviaOwnershipPercentError");
    if (!sources.length) next.sources = _t("pleaseSelectAtLeastOneOption");
    if (!signed) next.signature = _t("viviaAcceptDeclarationError");
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const answer = (key, value = form[key]) => ({
    value,
    source: fieldSources[key] || "",
  });
  const groupedAnswer = (keys, value) => {
    const sourcesForFields = keys
      .map((key) => fieldSources[key])
      .filter(Boolean);
    return {
      value,
      source: sourcesForFields.includes(
        constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER
      )
        ? constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER
        : sourcesForFields[0] || "",
    };
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error(_t("viviaCompleteRequiredFields"));
      setTimeout(
        () =>
          document
            .querySelector(".cif-error")
            ?.closest(".cif-section")
            ?.scrollIntoView({
              behavior: "smooth",
            }),
        0
      );
      return;
    }
    const commonFunds = {
      sources: answer("sources", sources),
      other: answer("other_source"),
      country: answer("funds_country"),
      country_other: answer("funds_country_other"),
      payment_method: answer("payment_method"),
      banking_entity: answer("banking_entity"),
    };
    const data = {
      subject_type: isCompany ? "company" : "individual",
      source_of_funds: commonFunds,
      signature: {
        accepted: { value: true, source: "" },
        signer_name: {
          value: isCompany
            ? form.signatory_name
            : `${form.first_name} ${form.last_name}`.trim(),
          source: isCompany
            ? fieldSources.signatory_name || ""
            : fieldSources.first_name === fieldSources.last_name
              ? fieldSources.first_name || ""
              : constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER,
        },
        signed_at: { value: new Date().toISOString(), source: "" },
      },
    };
    if (isCompany)
      Object.assign(data, {
        company_information: {
          company_name: answer("company_name"),
          legal_form: answer("legal_form"),
          legal_form_other: answer("legal_form_other"),
          incorporation_date: answer("incorporation_date"),
          document_type: answer("company_document_type"),
          document_number: answer("company_document_number"),
          registered_address: answer("registered_address"),
          postal_code: answer("company_postal_code"),
          town: answer("company_town"),
          province: answer("company_province"),
          country: answer("company_country"),
        },
        beneficial_owners: {
          has_beneficial_owner: answer("has_beneficial_owner"),
          beneficial_owner_is_pep: answer("beneficial_owner_is_pep"),
          beneficial_owner_is_pep_relative_or_associate: answer(
            "beneficial_owner_is_pep_relative_or_associate"
          ),
          beneficial_owners: groupedAnswer(
            ["beneficial_owner_name", "beneficial_owner_nif"],
            form.has_beneficial_owner
              ? [
                  {
                    full_name: form.beneficial_owner_name,
                    nif: form.beneficial_owner_nif,
                  },
                ]
              : []
          ),
          administration_type: answer("administration_type"),
          administrators: groupedAnswer(
            ["administrator_name", "administrator_nif"],
            form.has_beneficial_owner === false
              ? [
                  {
                    full_name: form.administrator_name,
                    nif: form.administrator_nif,
                  },
                ]
              : []
          ),
          pep_relationship: answer("owner_pep_relationship"),
          pep_person: answer("owner_pep_person"),
          pep_position: answer("owner_pep_position"),
          pep_country: answer("owner_pep_country"),
          pep_relative_position: answer("owner_pep_relative_position"),
          pep_relative_country: answer("owner_pep_relative_country"),
        },
        signatory: {
          full_name: answer("signatory_name"),
          document_number: answer("signatory_document_number"),
          signing_capacity: answer("signing_capacity"),
          company_position: answer("company_position"),
          phone: answer("signatory_phone"),
          email: answer("signatory_email"),
        },
        company_activity: {
          description: answer("activity_description"),
          is_listed_company: answer("is_listed_company"),
          belongs_to_company_group: answer("belongs_to_company_group"),
          group_name: answer("group_name"),
        },
      });
    else
      Object.assign(data, {
        personal_information: {
          first_name: answer("first_name"),
          last_name: answer("last_name"),
          full_name: {
            value: `${form.first_name} ${form.last_name}`.trim(),
            source:
              fieldSources.first_name === fieldSources.last_name
                ? fieldSources.first_name || ""
                : constants.CLIENT_IDENTIFICATION_SOURCES.EDITED_BY_USER,
          },
          document_type: answer("document_type", form.document_type),
          document_type_other: answer("document_type_other"),
          document_number: answer("document_number"),
          country_of_birth: answer("country_of_birth"),
          country_of_residence: answer("country_of_residence"),
          address: answer("address"),
          postal_code: answer("postal_code"),
          town: answer("town"),
          province: answer("province"),
          phone: answer("phone"),
          email: answer("email"),
          operation_capacity: answer("operation_capacity"),
          representative_name: answer("representative_name"),
          representative_nif: answer("representative_nif"),
        },
        public_positions: {
          is_pep: answer("is_pep"),
          is_pep_relative_or_associate: answer("is_pep_relative_or_associate"),
          pep_relationship: answer("pep_relationship"),
          pep_position: answer("pep_position"),
          pep_country: answer("pep_country"),
          pep_relative_position: answer("pep_relative_position"),
          pep_relative_country: answer("pep_relative_country"),
        },
        professional_activity: {
          employment_status: answer("employment_status"),
          annual_income: answer("annual_income"),
          employer_name: answer("employer_name"),
          job_position: answer("job_position"),
          activity: answer("professional_activity"),
          employer_activity: answer("professional_activity"),
          business_name: answer("business_name"),
          business_activity: answer("business_activity"),
          ownership_percent: answer("ownership_percent"),
          business_start_year: answer("business_start_year"),
          annual_turnover: answer("annual_turnover"),
          previous_activity: answer("previous_activity"),
          employment_status_other: answer("employment_status_other"),
        },
      });
    try {
      setSubmitting(true);
      await axiosInstance.post(
        `${process.env.REACT_APP_SERVER_URL}/tenants/client-identification-data`,
        {
          workspace_uuid: workspace?.uuid,
          application_id: applicationId,
          guarantor_for_tenant_id: guarantorForTenantId,
          landlord_email: landlordEmail,
          client_identification_data: data,
        }
      );
      await onSubmitted();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          _t("viviaSaveIdentificationError")
      );
    } finally {
      setSubmitting(false);
    }
  };
  if (loadingPrefill) {
    return (
      <div className="cif-overlay">
        <div className="cif-loading-page">
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              iconStart={<Icon name="close" />}
              className="cif-close"
              aria-label={_t("close")}
              onClick={onClose}
            />
          )}
          <div className="cif-loading" role="status" aria-live="polite">
            <img src={SkorDefaultLoader} alt="" aria-hidden="true" />
            <strong>{_t("viviaFetchingYourData")}</strong>
          </div>
        </div>
      </div>
    );
  }
  const companySections = (
    <>
      <FormSection
        number={1}
        total={total}
        title={_t("viviaCompanyInformation")}
        pending={companySectionPending.information}
      >
        <div className="cif-grid">
          {field("company_name", _t("companyName"), input("company_name"), {
            wide: true,
          })}
          {field(
            "legal_form",
            _t("viviaLegalForm"),
            select("legal_form", [
              ["SA", _t("viviaPublicLimitedCompany")],
              ["SL", _t("viviaLimitedLiabilityCompany")],
              ["OTHER", _t("viviaOther")],
            ])
          )}
          {form.legal_form === "OTHER" &&
            field(
              "legal_form_other",
              _t("viviaSpecifyLegalForm"),
              input("legal_form_other")
            )}
          {field(
            "incorporation_date",
            _t("viviaDateOfIncorporation"),
            input("incorporation_date", "date")
          )}
          {field(
            "company_document_type",
            _t("documentType"),
            select("company_document_type", [
              ["CIF_NIF", _t("viviaTaxIdCifNif")],
              ["OTHER", _t("viviaOther")],
            ])
          )}
          {field(
            "company_document_number",
            _t("dni_nie"),
            input("company_document_number", "text", {
              maxLength: 9,
            })
          )}
          {field(
            "registered_address",
            _t("viviaRegisteredAddress"),
            input("registered_address"),
            {
              wide: true,
            }
          )}
          {field(
            "company_postal_code",
            _t("postcode"),
            input("company_postal_code", "text", {
              maxLength: 6,
            })
          )}
          {field("company_town", _t("viviaCity"), input("company_town"))}
          {field(
            "company_province",
            _t("viviaRegionState"),
            input("company_province")
          )}
          {field("company_country", _t("country"), input("company_country"))}
        </div>
      </FormSection>
      <FormSection
        number={2}
        total={total}
        title={_t("viviaBeneficialOwners")}
        pending={companySectionPending.owners}
        description={_t("viviaBeneficialOwnersDescription")}
      >
        <Choice
          label={_t("viviaHasBeneficialOwner")}
          name="owner"
          value={form.has_beneficial_owner}
          onChange={(v) => update("has_beneficial_owner", v)}
          sourceNote={sourceNote("has_beneficial_owner")}
          yes={yes}
          no={no}
        />
        {form.has_beneficial_owner === true && (
          <div className="cif-grid">
            {field(
              "beneficial_owner_name",
              _t("viviaBeneficialOwnerName"),
              input("beneficial_owner_name")
            )}
            {field(
              "beneficial_owner_nif",
              _t("viviaTaxIdNif"),
              input("beneficial_owner_nif")
            )}
          </div>
        )}
        {form.has_beneficial_owner === false && (
          <div className="cif-grid">
            {field(
              "administration_type",
              _t("viviaAdministrationType"),
              select("administration_type", [
                ["SOLE", _t("viviaSoleAdministrator")],
                ["JOINT_AND_SEVERAL", _t("viviaJointAndSeveralAdministrators")],
                ["JOINT", _t("viviaJointAdministrators")],
                ["BOARD", _t("viviaBoardOfDirectors")],
              ])
            )}
            {field(
              "administrator_name",
              _t("viviaAdministratorName"),
              input("administrator_name")
            )}
            {field(
              "administrator_nif",
              _t("viviaTaxIdNif"),
              input("administrator_nif")
            )}
          </div>
        )}
        <Choice
          label={_t("viviaBeneficialOwnerPep")}
          name="owner-pep"
          value={form.beneficial_owner_is_pep}
          onChange={(v) => update("beneficial_owner_is_pep", v)}
          sourceNote={sourceNote("beneficial_owner_is_pep")}
          yes={yes}
          no={no}
        />
        {form.beneficial_owner_is_pep === true && (
          <div className="cif-grid">
            {field(
              "owner_pep_person",
              _t("viviaPepPerson"),
              input("owner_pep_person")
            )}
            {field(
              "owner_pep_position",
              _t("viviaPepPosition"),
              input("owner_pep_position")
            )}
            {field(
              "owner_pep_country",
              _t("viviaPepCountry"),
              input("owner_pep_country")
            )}
          </div>
        )}
        <Choice
          label={_t("viviaBeneficialOwnerRelated")}
          name="owner-related"
          value={form.beneficial_owner_is_pep_relative_or_associate}
          onChange={(v) =>
            update("beneficial_owner_is_pep_relative_or_associate", v)
          }
          sourceNote={sourceNote(
            "beneficial_owner_is_pep_relative_or_associate"
          )}
          yes={yes}
          no={no}
        />
        {form.beneficial_owner_is_pep_relative_or_associate === true && (
          <div className="cif-grid">
            {field(
              "owner_pep_relationship",
              _t("viviaPepRelationship"),
              input("owner_pep_relationship")
            )}
            {field(
              "owner_pep_relative_position",
              _t("viviaPepPosition"),
              input("owner_pep_relative_position")
            )}
            {field(
              "owner_pep_relative_country",
              _t("viviaPepCountry"),
              input("owner_pep_relative_country")
            )}
          </div>
        )}
      </FormSection>
      <FormSection
        number={3}
        total={total}
        title={_t("viviaCompanySignatory")}
        pending={companySectionPending.signatory}
      >
        <div className="cif-grid">
          {field("signatory_name", _t("fullName"), input("signatory_name"))}
          {field(
            "signatory_document_number",
            _t("viviaTaxIdNif"),
            input("signatory_document_number", "text", {
              maxLength: 9,
            })
          )}
          {field(
            "signing_capacity",
            _t("viviaSignsAs"),
            select("signing_capacity", [
              ["ADMINISTRATOR", _t("viviaAdministrator")],
              [
                "JOINT_AND_SEVERAL_ATTORNEY",
                _t("viviaJointAndSeveralAttorney"),
              ],
              ["JOINT_ATTORNEY", _t("viviaJointAttorney")],
              ["AGENT_OR_INTERMEDIARY", _t("viviaAgentOrIntermediary")],
            ])
          )}
          {field(
            "company_position",
            _t("viviaCompanyPosition"),
            input("company_position")
          )}
          {field(
            "signatory_phone",
            _t("phoneNumber"),
            phoneInput("signatory_phone")
          )}
          {field(
            "signatory_email",
            _t("email"),
            input("signatory_email", "email")
          )}
        </div>
      </FormSection>
      <FormSection
        number={4}
        total={total}
        title={_t("viviaCompanyActivity")}
        pending={companySectionPending.activity}
      >
        {field(
          "activity_description",
          _t("viviaActivityDescription"),
          input("activity_description")
        )}
        <Choice
          label={_t("viviaListedCompany")}
          name="listed"
          value={form.is_listed_company}
          onChange={(v) => update("is_listed_company", v)}
          sourceNote={sourceNote("is_listed_company")}
          yes={yes}
          no={no}
        />
        <Choice
          label={_t("viviaCompanyGroup")}
          name="group"
          value={form.belongs_to_company_group}
          onChange={(v) => update("belongs_to_company_group", v)}
          sourceNote={sourceNote("belongs_to_company_group")}
          yes={yes}
          no={no}
        />
        {form.belongs_to_company_group === true &&
          field("group_name", _t("viviaGroupName"), input("group_name"))}
      </FormSection>
    </>
  );
  const individualSections = (
    <>
      <FormSection
        number={1}
        total={total}
        title={_t("personalInformation")}
        pending={individualSectionPending.personal}
      >
        <div className="cif-grid">
          {field("first_name", _t("firstName"), input("first_name"))}
          {field("last_name", _t("lastName"), input("last_name"))}
          {field(
            "document_type",
            _t("documentType"),
            select("document_type", [
              ["DNI", _t("viviaNationalIdDni")],
              ["NIE", _t("nie")],
              ["PASSPORT", _t("passport")],
            ])
          )}
          {field(
            "document_number",
            _t("dni_nie"),
            input("document_number", "text", {
              maxLength: 9,
            })
          )}
          {field(
            "country_of_birth",
            _t("viviaCountryOfBirth"),
            input("country_of_birth")
          )}
          {field(
            "country_of_residence",
            _t("viviaCountryOfResidence"),
            input("country_of_residence")
          )}
          {field("address", _t("address"), input("address"))}
          {field(
            "postal_code",
            _t("postcode"),
            input("postal_code", "text", {
              maxLength: 6,
            })
          )}
          {field("town", _t("viviaCity"), input("town"))}
          {field("province", _t("viviaRegionState"), input("province"))}
          {field("phone", _t("phoneNumber"), phoneInput("phone"))}
          {field("email", _t("email"), input("email", "email"))}
        </div>
        <Segment
          label={_t("viviaOperationCapacity")}
          value={form.operation_capacity}
          onChange={(v) => update("operation_capacity", v)}
          sourceNote={sourceNote("operation_capacity")}
          options={[
            ["DIRECT", _t("viviaDirectly")],
            ["REPRESENTATIVE", _t("viviaWithRepresentative")],
          ]}
        />
        {form.operation_capacity === "REPRESENTATIVE" && (
          <div className="cif-grid">
            {field(
              "representative_name",
              _t("fullName"),
              input("representative_name")
            )}
            {field(
              "representative_nif",
              _t("representativeId"),
              input("representative_nif")
            )}
          </div>
        )}
      </FormSection>
      <FormSection
        number={2}
        total={total}
        title={_t("viviaPublicPositions")}
        pending={individualSectionPending.publicPositions}
        description={_t("viviaPublicPositionsDescription")}
      >
        <Choice
          label={_t("viviaAreYouPep")}
          name="pep"
          value={form.is_pep}
          onChange={(v) => update("is_pep", v)}
          sourceNote={sourceNote("is_pep")}
          yes={yes}
          no={no}
        />
        {form.is_pep === true && (
          <div className="cif-grid">
            {field(
              "pep_position",
              _t("viviaPepPosition"),
              input("pep_position")
            )}
            {field("pep_country", _t("viviaPepCountry"), input("pep_country"))}
          </div>
        )}
        <Choice
          label={_t("viviaAreYouPepRelated")}
          name="pep-related"
          value={form.is_pep_relative_or_associate}
          onChange={(v) => update("is_pep_relative_or_associate", v)}
          sourceNote={sourceNote("is_pep_relative_or_associate")}
          yes={yes}
          no={no}
        />
        {form.is_pep_relative_or_associate === true && (
          <div className="cif-grid">
            {field(
              "pep_relationship",
              _t("viviaPepRelationship"),
              input("pep_relationship")
            )}
            {field(
              "pep_relative_position",
              _t("viviaPepPosition"),
              input("pep_relative_position")
            )}
            {field(
              "pep_relative_country",
              _t("viviaPepCountry"),
              input("pep_relative_country")
            )}
          </div>
        )}
      </FormSection>
      <FormSection
        number={3}
        total={total}
        title={_t("viviaProfessionalActivity")}
        pending={individualSectionPending.professional}
      >
        <div className="cif-grid">
          {field(
            "employment_status",
            _t("employmentStatus"),
            select(
              "employment_status",
              employmentOptions.map(([v, k]) => [v, _t(k)])
            )
          )}
          {!employmentStatus.includes("self") &&
            employmentStatus !== String(constants.retired).toLowerCase() &&
            employmentStatus !== "other" && (
              <>
                {field(
                  "annual_income",
                  _t("annualIncome"),
                  select(
                    "annual_income",
                    incomeOptions.map(([value, key]) => [value, _t(key)]),
                    false
                  )
                )}
                {field("employer_name", _t("company"), input("employer_name"))}
                {field("job_position", _t("position"), input("job_position"))}
                {field(
                  "professional_activity",
                  _t("viviaCompanyActivity"),
                  input("professional_activity"),
                  { wide: true }
                )}
              </>
            )}
          {employmentStatus.includes("self") && (
            <>
              {field(
                "business_name",
                _t("viviaBusinessName"),
                input("business_name")
              )}
              {field(
                "business_activity",
                _t("viviaBusinessActivity"),
                input("business_activity")
              )}
              {field(
                "ownership_percent",
                _t("viviaOwnershipPercent"),
                input("ownership_percent", "number", { min: 0, max: 100 })
              )}
              {field(
                "business_start_year",
                _t("viviaBusinessStartYear"),
                input("business_start_year", "number", { min: 1900 })
              )}
              {field(
                "annual_turnover",
                _t("viviaAnnualTurnover"),
                input("annual_turnover", "number", { min: 0 }),
                { wide: true }
              )}
            </>
          )}
          {employmentStatus === String(constants.retired).toLowerCase() &&
            field(
              "previous_activity",
              _t("viviaPreviousActivity"),
              input("previous_activity"),
              { wide: true }
            )}
          {employmentStatus === "other" &&
            field(
              "employment_status_other",
              _t("viviaDescribeSituation"),
              input("employment_status_other"),
              { wide: true }
            )}
        </div>
      </FormSection>
    </>
  );
  return (
    <div className="cif-overlay">
      <form className="cif-page" onSubmit={handleSubmit} noValidate>
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            iconStart={<Icon name="close" />}
            className="cif-close"
            aria-label={_t("close")}
            onClick={onClose}
            disabled={submitting}
          />
        )}
        <header className="cif-topbar">
          <strong>skor</strong>
          <span>
            {_t("viviaLastStepBeforeSharing")} {workspace?.name || ""}
          </span>
        </header>
        <div className="cif-intro">
          <h1>{_t("viviaClientIdentificationForm")}</h1>
          <p>
            {_t(
              isCompany
                ? "viviaCompanyIdentificationIntro"
                : "viviaIndividualIdentificationIntro"
            )}
          </p>
          <div>
            <span>
              {`${user?.fname || ""} ${user?.lname || ""}`.trim()} ·{" "}
              {user?.email}
            </span>
            <span>
              <b>*</b> {_t("viviaMandatoryField")}
            </span>
          </div>
        </div>
        {isCompany ? companySections : individualSections}
        <FormSection
          number={isCompany ? 5 : 4}
          total={total}
          title={_t("viviaSourceOfFunds")}
          pending={fundsPending}
        >
          <p className="cif-question">
            {_t("viviaFundsOrigin")} <b>*</b>{" "}
            <small>{_t("viviaMultipleOptions")}</small>
          </p>
          <div className="cif-funds">
            {fundOptions.map(([value, key]) => (
              <Checkbox
                key={value}
                checked={sources.includes(value)}
                onChange={() => toggleSource(value)}
                label={_t(key)}
              />
            ))}
          </div>
          {sourceNote("sources")}
          {errors.sources && (
            <small className="cif-error cif-block-error">
              {errors.sources}
            </small>
          )}
          {sources.includes("OTHER") &&
            field(
              "other_source",
              _t("viviaOtherSource"),
              input("other_source")
            )}
          <div className="cif-grid">
            {field(
              "funds_country",
              _t("viviaFundsCountry"),
              select("funds_country", [
                ["SPAIN", _t("spain")],
                ["EU_GROUP", _t("viviaEuCountryGroup")],
                ["US_GROUP", _t("viviaUsCountryGroup")],
                ["OTHER", _t("viviaOther")],
              ]),
              {
                wide: true,
              }
            )}
            {form.funds_country === "OTHER" &&
              field(
                "funds_country_other",
                _t("viviaSpecifyCountry"),
                input("funds_country_other"),
                { wide: true }
              )}
            {field(
              "payment_method",
              _t("viviaPaymentMethod"),
              select("payment_method", [
                ["BANK_TRANSFER", _t("viviaBankTransfer")],
                ["BANK_CHEQUE", _t("viviaBankCheque")],
                ["BEARER", _t("viviaBearerPayment")],
              ])
            )}
            {form.payment_method === "BANK_TRANSFER" &&
              field(
                "banking_entity",
                _t("viviaBankingEntity"),
                input("banking_entity")
              )}
          </div>
        </FormSection>
        <FormSection
          number={total}
          total={total}
          title={_t("viviaSignature")}
          pending={signed ? 0 : 1}
        >
          <p className="cif-description">{_t("viviaSignatureDescription")}</p>
          <Checkbox
            checked={signed}
            onChange={(checked) => {
              setSigned(checked);
              setErrors((old) => ({
                ...old,
                signature: undefined,
              }));
            }}
            label={
              <>
                {_t(
                  isCompany
                    ? "viviaCompanyDeclaration"
                    : "viviaIndividualDeclaration"
                )}{" "}
                <b>*</b>
              </>
            }
          />
          {errors.signature && (
            <small className="cif-error cif-block-error">
              {errors.signature}
            </small>
          )}
          <div className="cif-legal-links">
            <a
              href="/pdf/consents/KYC_CLIENT_IDENTIFICATION_template.pdf"
              target="_blank"
              rel="noreferrer"
            >
              {_t("viviaViewForm")}
            </a>
            <a
              href="https://skortorent.com/privacy-policy.html"
              target="_blank"
              rel="noreferrer"
            >
              {_t("privacyPolicy")}
            </a>
          </div>
        </FormSection>
        <footer className="cif-footer">
          <div>
            <strong>
              {missing} {_t("viviaMandatoryAnswersMissing")}
            </strong>
            {missing > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={() =>
                  document
                    .querySelector('.cif-section[data-pending="true"]')
                    ?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                }
              >
                {_t("viviaGoToFirstPending")}
              </Button>
            )}
          </div>
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={submitting || missing > 0}
          >
            {submitting ? _t("viviaSaving") : _t("viviaSignAndShare")}
          </Button>
          <span>{_t("viviaLegallyBinding")}</span>
        </footer>
      </form>
    </div>
  );
}
